import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { uniqueId } from 'lodash-es';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  OrderedCapacityFinalExcess,
  OrderedCapacityFinalExcessGrid,
  OrderedCapacityFinalExcessRecord,
} from './types';

export const orderedCapacityFinalExcessApi = createApi({
  reducerPath: 'orderedCapacityFinalExcessApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/ordered-capacity-final-excesses`,
  }),
  tagTypes: [
    'OrderedCapacityFinalExcesses',
    'OrderedCapacityFinalExcess',
    'OrderedCapacityFinalExcessRecords',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getOrderedCapacityFinalExcesses: builder.infiniteQuery<
      OrderedCapacityFinalExcessGrid[],
      FiltersGrid & { name: string },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/query',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: ['OrderedCapacityFinalExcesses'],
    }),
    getOrderedCapacityFinalExcessById: builder.query<
      OrderedCapacityFinalExcess,
      string
    >({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: ['OrderedCapacityFinalExcess'],
    }),
    getOrderedCapacityFinalExcessesRecordsI: builder.infiniteQuery<
      OrderedCapacityFinalExcessRecord[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/records',
        params: { ...queryArg, ...pageParam },
      }),
      transformResponse(records: OrderedCapacityFinalExcessRecord[]) {
        return records.map(record => ({
          ...record,
          id: uniqueId(),
        }));
      },
      providesTags: ['OrderedCapacityFinalExcessRecords'],
    }),
    removeOrderedCapacityFinalExcess: builder.mutation<string, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => ['OrderedCapacityFinalExcess'],
    }),
    recalculateOrderedCapacityFinalExcess: builder.mutation<void, string>({
      query: id => ({
        url: `/recalculate/${id}`,
        method: 'POST',
      }),
      invalidatesTags: () => [
        'OrderedCapacityFinalExcess',
        'OrderedCapacityFinalExcessRecords',
      ],
    }),
  }),
});

export const {
  useGetOrderedCapacityFinalExcessByIdQuery,
  useRemoveOrderedCapacityFinalExcessMutation,
  useRecalculateOrderedCapacityFinalExcessMutation,
} = orderedCapacityFinalExcessApi;

export const useGetOrderedCapacityFinalExcessesInfiniteQuery =
  normalizeInfiniteQueryData(
    orderedCapacityFinalExcessApi.useGetOrderedCapacityFinalExcessesInfiniteQuery,
  );

export const useGetOrderedCapacityFinalExcessesRecordsInfiniteQuery =
  normalizeInfiniteQueryData(
    orderedCapacityFinalExcessApi.useGetOrderedCapacityFinalExcessesRecordsIInfiniteQuery,
  );
